<template>
  <div class="flex flex-col place-content-center">
    <div class="flex flex-col gap-2">
      <h2 class="text-5xl font-bold leading-[3.5rem] tracking-[-0.04rem] text-neutral-800">
        {{ t('auth.reset_password.title') }}
      </h2>
      <p class="text-[1.125rem] font-semibold leading-[1.4375rem] tracking-[-0.02rem] text-neutral-500">
        {{ t('auth.reset_password.under_title') }}
      </p>
    </div>

    <form class="mt-12 flex flex-col place-content-center" @submit.prevent="onSubmit">
      <infuse-field :label="t('auth.reset_password.reset_code')">
        <infuse-input
          v-model="form.code"
          :placeholder="t('auth.reset_password.reset_code')"
          :tabindex="1"
          required
          autofocus
        />
      </infuse-field>

      <infuse-field :label="t('auth.common.username')">
        <infuse-input v-model="form.username" type="email" placeholder="name@company.com" :tabindex="2" required />
      </infuse-field>

      <infuse-field :label="t('auth.common.password')">
        <infuse-input
          v-model="form.password"
          type="password"
          :placeholder="t('auth.common.password')"
          :tabindex="3"
          required
        />
      </infuse-field>

      <infuse-field :label="t('auth.common.confirm_password')">
        <infuse-input
          v-model="form.confirmPassword"
          type="password"
          :placeholder="t('auth.common.confirm_password')"
          :tabindex="4"
          required
        />
      </infuse-field>

      <PasswordPolicyChecks
        v-if="passwordPolicy"
        ref="passwordPolicyRef"
        :password="form.password"
        :password-policy="passwordPolicy"
      />

      <div v-if="submitted && !passwordsMatch" class="text-sm text-red-500">
        {{ t('auth.common.passwords_dont_match') }}
      </div>

      <div class="mt-2 flex items-center justify-end gap-4">
        <router-link :to="{ name: 'auth-login' }">
          <label class="cursor-pointer text-xs font-bold leading-4 text-secondary-500">
            {{ t('auth.reset_password.back_button') }}
          </label>
        </router-link>

        <infuse-button size="large" :loading="loading" type="submit" :tabindex="5">
          {{ t('auth.reset_password.submit') }}
        </infuse-button>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseField, InfuseInput } from '@infuse/ui/components';
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthenticator } from '@infuse/auth/services';
import PasswordPolicyChecks from './component/PasswordPolicyChecks.vue';

interface Props {
  loading?: boolean;
}

interface Emits {
  (e: 'submit', form: { username: string; password: string; code: string }): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();

const form = reactive({
  username: '',
  password: '',
  confirmPassword: '',
  code: ''
});

const submitted = ref(false);
const passwordPolicyRef = ref();
const passwordsMatch = computed(() => form.password === form.confirmPassword);

const passwordPolicy = computed(() => useAuthenticator().getPasswordPolicy());

function onSubmit() {
  submitted.value = true;
  if (!passwordsMatch.value) return;
  if (passwordPolicy.value && passwordPolicyRef.value && !passwordPolicyRef.value.allPasswordPolicyChecksPassed) return;
  emit('submit', { username: form.username, password: form.password, code: form.code });
}
</script>
